import _ from 'lodash'
import parseISO from 'date-fns/parseISO'
import React, { useRef, forwardRef } from 'react'
import Editor, { theme as EditorTheme } from '@gogovapps/rich-markdown-editor';
import { useDispatch } from 'react-redux';
import { Label, Checkbox, Input, Select, TextArea, Form, Dropdown, Icon } from 'semantic-ui-react'
import { Controller } from 'react-hook-form'
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { SafeMoment } from './SafeMoment'
import { humanizeString } from '../lib/humanize-string'
import { s3Url } from '../utils/s3_url'
import { upload } from '../actions/upload'
import AppSettings from '../config'
import Tooltip from './Tooltip';
import { ControlledSelect } from './ControlledSelect'
import { capitalizeFirstLetter } from '../utils/functions';
import { DropDownMultiTypeSelector, DropDownRowSelector, DropDownSelector } from './permits/DropDownFilter';
export { ControlledSelect }

import './ControlledForm.scss'
import { toDate } from '../lib/date-util';

const Readonly = ({ as, value }:any) => {
  const isEmpty = [undefined, ''].includes(value)
  return React.createElement(as, !isEmpty ? {} : {style: { color: 'gray' }}, [isEmpty ? "N/A" : value])
}
const isErrorSelector = (errors:any) => (fieldName:string) => !!_.get(errors, `${fieldName}.message`);
const errorMessageSelector = (errors:any) => (fieldName:string) => isErrorSelector(fieldName) ?
  _.get(errors, `${fieldName}.message`).replace(fieldName, humanizeString(fieldName)) : null;

interface IControlledWysiwyg {
  control: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  site: string
  folder: string
  bucket: string
  height?: string
  defaultValue?: any
}

export const ControlledWysiwyg = (props:IControlledWysiwyg) => {
  const dispatch = useDispatch();
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      render={({ onChange, name }) => {
        const editorRef:any = useRef();
        return (
          <Form.Field error={isError}>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <div
              tabIndex={-1}
              role='textbox'
              onClick={(e) => {
                if((e.target as any).getAttribute('placeholder') === 'Search or paste a link…') {
                  return;
                }
                if(!editorRef.current.view.focused) {
                  editorRef.current.focusAtEnd()
                }
              }}
              style={{
                minHeight: props.height,
                  background: isError ? '#fff6f6' : 'white',
                  borderRadius: '4px',
                  border: isError ? '1px solid #e0b4b4' : '1px solid rgba(34,36,38,.15)',
                  color: isError ? '#9f3a38' : 'inherit',
                  padding: '6px',
                  marginLeft: '24px'
              }}>
              <Editor
                ref={editorRef}
                placeholder={props.placeholder}
                defaultValue={props.defaultValue}
                disabledToolbarItems={['mark']}
                disabledBlockMenuItems={['container_notice', 'table', 'checkbox_list', 'code_block']}
                theme={{
                  ...EditorTheme,
                  zIndex: '10000'
                } as any}
                uploadImage={async (file:any) => {
                  const uploadOptions = {
                    id: 'controlled-wysiwg',
                    site: props.site,
                    file: file,
                    folder: props.folder,
                    bucket: props.bucket
                  };
                  const uploadResponse = (await dispatch(upload(uploadOptions))) as any
                  return s3Url(uploadResponse.payload, AppSettings.CONTENT_URL)
                }}
                onChange={(getValue:any) => {
                  onChange(removeExtraCarraigeReturns(getValue()))
                } }
              />
            </div>
          </Form.Field>
        )
      }}
    />
  )
}

// Removes errant '\' characters that show up instead of '\n'
export const removeExtraCarraigeReturns = (originalValue: any): string|null|undefined =>{
  if (originalValue === undefined) return null;
  if (originalValue === null) return null;
  let ret = originalValue.replace(/\\n/g, "\n");
  ret = ret.replace(/\n\\/g, "\n");
  ret = ret.replace(/^\\/, "\n")
  return ret;
}

interface IControlledTextArea {
  control: any
  name: string
  label?: string
  placeholder?: string
  readonly?: boolean
  required?: boolean
  errors?: any
  rows: number
  style?: any;
  rules?: any;
  defaultValue?: string
  onBlur?: any;
}

export const ControlledTextArea = (props: IControlledTextArea) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={Object.assign({}, { required: props.required && 'This field is required' }, props.rules)}
      render={({ onChange, value, name }) => {
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            {props.readonly && <Readonly as='p' value={value || props.defaultValue}/>}
            {!props.readonly && <TextArea
              id={props.name}
              style={props.style}
              placeholder={props.placeholder}
              rows={props.rows}
              error={isError}
              name={name}
              value={value}
              onChange={(e, { value }) => (onChange(value))}
              onBlur={props.onBlur}
            />}
          </>
        )
      }}
    />
  )
}

interface IControlledInput {
  control: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  readonly?: boolean
  required?: boolean
  defaultValue?: any
  type?: string
  action?: any
  style?: any
  disabled?: boolean
  rules?: any
  icon?: any
  onBlur?: any
}

export const ControlledInput = (props: IControlledInput) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={Object.assign({}, { required: props.required && 'This field is required' }, props.rules)}
      render={({ onChange, value, name }) => {
        return (
          <>
            {props.label && (
              <label htmlFor={name}>
                {props.label}
                {props.name === "min_days_notice" && (
                  <Tooltip>If set, applicants cannot select dates within the minimum days notice from the date they are applying.</Tooltip>
                )}
              </label>
            )}
            {props.readonly && <Readonly as='span' value={value || props.defaultValue}/>}
            {!props.readonly && <Input
              id={props.name}
              type={props.type}
              icon={props.icon}
              disabled={props.disabled}
              iconPosition={props.icon && 'left'}
              placeholder={props.placeholder}
              error={isError}
              name={name}
              defaultValue={props.defaultValue}
              value={value === undefined || value === null ? props.defaultValue : value}
              onBlur={props.onBlur}
              onChange={(e, {value}) => (onChange(value))}
              style={props.style}
              action={props.action}
            />}
            {isError && <Label basic prompt pointing='above'>{errorMessageSelector(props.errors)(name)}</Label>}
          </>
        )
      }}
    />
  )
}

interface IControlledSelect {
  control: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  readonly?: boolean
  search?: boolean
  selectProps?: any
  defaultValue?: any
  disabled?: boolean
  required?: boolean
  inline?: boolean
}

export const ControlledLabelSelect = (props:IControlledSelect) => {
  const step_types: any[] = [ 'review', 'document', 'inspection' ];

  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={{ required: props.required && 'This field is required' }}
      render={({ onChange, value, name }) => {
        return (
          <div className="ControlledLabelSelect">
            {props.label && <label className="step_type_label" htmlFor={name}>{props.label}</label>}
            <div className="selector">
              {!props.readonly && step_types.map((type: string) => (
                <Label
                  circular
                  key={type}
                  className={`step_type_selector ${value === type ? 'active' : ''}`}
                  onClick={() => (onChange(type))}
                >
                  {capitalizeFirstLetter(type)} Step
                </Label>
              ))}
            </div>
            {!value && (
              <Label basic prompt pointing='above'>This field is required</Label>
            )}
          </div>
        )
      }}
    />
  )
}

interface IControlledTagInput {
  control: any
  name: string
  label?: string
  placeholder: string
  noResultsMessage?: string
  errors?: any
  rules?:any
  selectProps?: any
  required?: boolean
  defaultValue?: any
  singleSelect?: boolean
  type?: string
  title?: any
}

export const ControlledTagInput = (props:IControlledTagInput) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        const options = value?.map((item:any) => ({key: item, text: item, value: item}))
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <Select search selection multiple allowAdditions
              options={options}
              name={name}
              error={isError}
              value={value || []}
              placeholder={props.placeholder}
              required={props.required}
              noResultsMessage={props.noResultsMessage}
              onChange={(e:any, data:any) => onChange(data.value) }
              {...props.selectProps}
            />
          </>
        )
      }}>
    </Controller>
  );
}

interface IControlledCheckbox {
  name: string
  control?: any
  label?: string
  errors?: any
  disabled?: boolean
  required?: boolean
  readonly?: boolean
  radio?: boolean
  toggle?: boolean
  defaultValue?: boolean
  onBlur?: any
}

export const ControlledCheckbox = (props: IControlledCheckbox) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={{ required: props.required && 'This field is required' }}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        if(props.readonly) { return <Icon size='large' name={value == true ? 'check square outline' : 'square outline'} /> }
        return (
          <>
            <Checkbox
              name={name}
              label={props.label}
              error={isError}
              checked={value == true}
              radio={props.radio}
              toggle={props.toggle}
              disabled={props.disabled || props.readonly}
              onChange={(e, { checked }) => {
                onChange(checked);
                if (props.onBlur) {
                  props.onBlur();
                }
              }}
            />
          </>
        )
      }}
    />
  )
}

interface IControlledDatePicker extends Partial<ReactDatePickerProps>{
  control: any;
  name: string;
  label?: string;
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;
  errors?: any;
  defaultValue?: any;
  min?: any;
  citizen? : boolean;
  onBlur?: any;
}

export const ControlledDatePicker = (props: IControlledDatePicker) => {
  const datePickerProps:Pick<IControlledDatePicker, keyof ReactDatePickerProps> = props;
  const isError = isErrorSelector(props.errors)(props.name)
  let minDate: any = new Date();
  minDate.setDate(minDate.getDate() + parseInt(props.min));
  minDate = props.min && props.citizen ? minDate : null;
  if (parseInt(props.min) === 0) {
    minDate = undefined;
  }

  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={{ required: props.required && 'This field is required' }}
      render={({ onChange, value, name }) => {
        const readonlyValue = (value || props.defaultValue) ?
          <SafeMoment format={"MMM D, YYYY"}>{value || props.defaultValue}</SafeMoment> :
          <span style={{color:'gray'}}>N/A</span>
        return (
          <div className='ControlledFormDatePicker'>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            {props.readonly && readonlyValue}
            {!props.readonly && <DatePicker
              minDate={minDate}
              selected={toDate(value)}
              placeholderText={props.placeholder}
              onChange={(date:Date) => {
                onChange(date);
                if (props.onBlur) {
                  props.onBlur();
                }
              }}
              {...datePickerProps}
              required={false}
            />}
            {isError && <Label basic prompt pointing='above'>{errorMessageSelector(props.errors)(name)}</Label>}
          </div>
        )
      }}
    />
  )
}

export const ControlledDropdown = (props: IControlledTagInput) => {
  const { name, control, rules, defaultValue, label, singleSelect, placeholder, selectProps, type="single"} = props;
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ onChange, value, name }) => {
        return (
          <>
            {label && <label htmlFor={name} style={{fontWeight: 'bold'}}>{label}</label>}
            {type === 'single' && (
              <DropDownSelector
                fluid
                multiple
                search
                selection
                singleSelect={singleSelect}
                name={name}
                error={isError}
                closeOnChange={true}
                value={value}
                title='search'
                defaultTitle={placeholder}
                onChange={(data:any) => {
                  onChange(data)
                } }
                {...selectProps}
              />
            )}
            {type === 'multi' && (
              <DropDownMultiTypeSelector
                fluid
                multiple
                search
                selection
                singleSelect={singleSelect}
                name={name}
                error={isError}
                closeOnChange={true}
                value={value}
                title='search'
                defaultTitle={placeholder}
                onChange={(data:any) => {
                  onChange(data)
                } }
                {...selectProps}
              />
            )}
          </>
        )
      }}>
    </Controller>
  );
}

interface IControlledRowDropdown {
  control: any
  name: string
  label?: string
  placeholder: string
  noResultsMessage?: string
  errors?: any
  rules?:any
  selectProps?: any
  required?: boolean
  defaultValue?: any
  singleSelect?: boolean
  onChange?: (value: any) => void,
}

export const ControlledRowDropdown = (props: IControlledRowDropdown) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <DropDownRowSelector
              fluid
              multiple
              search
              selection
              singleSelect={props.singleSelect}
              name={name}
              error={isError}
              closeOnChange={true}
              value={value}
              title='citizens'
              defaultTitle={props.placeholder}
              onChange={(data:any) => {
                onChange(data)
              }}
              onChangeFilter={(filter:any) => {
                if (props.onChange) {
                  props.onChange(filter)
                }
              }}
              {...props.selectProps}
            />
          </>
        )
      }}>
    </Controller>
  );
}

interface IControlledDropdownV2 {
  control?: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  rules?:any
  clearable?: any
  allowAdditions?: any
  options: any
  required?: boolean
  renderLabel?: any
  style?: any
  icon?: string
  multiple?: any
  defaultValue?: any
  onChange?: (value: any) => void,
}

export const ControlledDropdownV2 = (props:IControlledDropdownV2) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      {...props}
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <Dropdown
              placeholder={props.placeholder}
              search
              fluid
              selection
              renderLabel={props.renderLabel}
              multiple={props.multiple}
              defaultValue={props.defaultValue}
              clearable={props.clearable}
              allowAdditions={props.allowAdditions}
              icon={props.icon}
              name={name}
              error={isError}
              value={value}
              options={props.options}
              onChange={(e:any, data:any) => onChange(data.value) }
              style={props.style}
            />
          </>
        )}}>
    </Controller>
  );
}

interface IControlledMaskedPhone {
  control: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  readonly?: boolean
  required?: boolean
  defaultValue?: any
  type?: string
  action?: any
  style?: any
  disabled?: boolean
  rules?: any
  icon?: any
  onBlur?: any
}

export const ControlledMaskedPhone = (props: IControlledMaskedPhone) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={Object.assign({}, { required: props.required && 'This field is required' }, props.rules)}
      render={({ onChange, value, name }) => {
        const phoneNumberAutoFormat = (value: string) => {
          const number = value.trim().replace(/[^0-9]/g, "");
          if (number.length < 4) return number;
          if (number.length < 7) return number.replace(/(\d{3})(\d{1})/, "$1-$2");
          if (number.length < 11) return number.replace(/(\d{3})(\d{3})(\d{1})/, "$1-$2-$3");
          return number.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
        };
        return (
          <>
            {props.label && (
              <label htmlFor={name}>
                {props.label}
                {props.name === "min_days_notice" && (
                  <Tooltip>If set, applicants cannot select dates within the minimum days notice from the date they are applying.</Tooltip>
                )}
              </label>
            )}
            {props.readonly && <Readonly as='span' value={value || props.defaultValue}/>}
            {!props.readonly && <Input
              id={props.name}
              type={props.type}
              icon={props.icon}
              disabled={props.disabled}
              iconPosition={props.icon && 'left'}
              placeholder={props.placeholder}
              error={isError}
              name={name}
              defaultValue={props.defaultValue}
              value={value === undefined || value === null ? props.defaultValue : phoneNumberAutoFormat(value)}
              onBlur={props.onBlur}
              onChange={(e, {value}) => (onChange(value))}
              style={props.style}
              action={props.action}
            />}
            {isError && <Label basic prompt pointing='above'>{errorMessageSelector(props.errors)(name)}</Label>}
          </>
        )
      }}
    />
  )
}
